import React, { useEffect, useState } from 'react';
import { ArrowLeft, Lightbulb } from 'lucide-react';
export interface Question {
  id: string;
  label: string;
  question: string;
  subtext: string;
  probing: string[];
}
interface QuestionScreenProps {
  question: Question;
  questionIndex: number;
  totalQuestions: number;
  value: string;
  onChange: (val: string) => void;
  onNext: () => void;
  onBack: () => void;
}
const ACTION_BAR_HEIGHT = 64;
export function QuestionScreen({
  question,
  questionIndex,
  totalQuestions,
  value,
  onChange,
  onNext,
  onBack
}: QuestionScreenProps) {
  const [timeLeft, setTimeLeft] = useState(60);
  useEffect(() => {
    setTimeLeft(60);
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [question.id]);
  const showExtraThinking = value.length > 0 && value.length < 50;
  const progressPercentage = (questionIndex + 1) / totalQuestions * 100;
  return <div className="flex flex-col w-full bg-brand-dark text-white relative" style={{
    minHeight: '100dvh',
    paddingBottom: ACTION_BAR_HEIGHT
  }} data-id="element-20">
      <div className="w-full pt-6 px-4" data-id="element-21">
        <div className="text-brand-accent text-xs font-medium mb-2 text-center" data-id="element-22">
          Question {questionIndex + 1} of {totalQuestions}
        </div>
        <div className="w-full h-1 bg-brand-dark border border-brand-accent/30 rounded-full overflow-hidden" data-id="element-23">
          <div className="h-full bg-brand-accent transition-all duration-300 ease-out" style={{
          width: `${progressPercentage}%`
        }} data-id="element-24" />
        </div>
      </div>

      <div className="flex-1 px-4 pt-8 flex flex-col" data-id="element-25">
        <div className="text-brand-accent text-sm font-bold tracking-widest uppercase mb-3" data-id="element-26">
          {question.label}
        </div>
        <h1 className="text-3xl font-bold text-white mb-2 leading-tight" data-id="element-27">
          {question.question}
        </h1>
        <p className="text-brand-surface/80 italic text-base mb-6" data-id="element-28">
          {question.subtext}
        </p>

        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder="Type your answer here..." className="w-full bg-white text-brand-dark rounded-xl p-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-accent resize-none shadow-inner" rows={4} data-id="element-29" />

        {showExtraThinking && <div className="mt-4 bg-white/5 border border-brand-accent/20 rounded-xl p-4" data-id="element-30">
            <div className="flex items-center gap-2 text-brand-accent mb-2" data-id="element-31">
              <Lightbulb size={16} data-id="element-32" />
              <span className="text-xs font-bold uppercase tracking-wider" data-id="element-33">
                Extra thinking
              </span>
            </div>
            <ul className="space-y-2" data-id="element-34">
              {question.probing.map((probe, idx) => <li key={idx} className="text-brand-surface/90 text-sm flex items-start gap-2" data-id="element-35">
                  <span className="text-brand-accent mt-0.5" data-id="element-36">•</span>
                  <span data-id="element-37">{probe}</span>
                </li>)}
            </ul>
          </div>}
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-[390px] mx-auto bg-brand-dark border-t border-white/10 px-4 flex items-center justify-between z-20" style={{
      height: ACTION_BAR_HEIGHT
    }} data-id="element-38">
        <button onClick={onBack} disabled={questionIndex === 0} className={`p-3 rounded-lg transition-colors ${questionIndex === 0 ? 'opacity-0 pointer-events-none' : 'text-white hover:bg-white/10'}`} aria-label="Go back" data-id="element-39">
          <ArrowLeft size={24} data-id="element-40" />
        </button>

        <div className="text-brand-surface/60 font-mono text-sm font-medium" data-id="element-41">
          0:{timeLeft.toString().padStart(2, '0')}
        </div>

        <button onClick={onNext} className="bg-brand-cta text-brand-dark px-8 py-3 rounded-lg font-bold text-base hover:bg-brand-cta/90 transition-colors shadow-lg" data-id="element-42">
          Next
        </button>
      </div>
    </div>;
}